/* ═══════════════════════════════════════════════════════════════════════════════
   Tour Registry: Central Registration Point

   All tours are registered here. To add a new tour:
   1. Create a tour definition file in src/lib/tours/ (e.g., myFeatureTour.ts)
   2. Import and add it to TOUR_REGISTRY below
   3. That's it. The tour system handles the rest.

   PERSISTENCE MODEL (v2.5+):
   - PRIMARY: Supabase `profiles.claims_completed_tours` JSONB, survives cache clears
   - SECONDARY: localStorage `seq_claims_tours_completed`, fast local cache
   - On load: merge both sources (server wins conflicts)
   - On completion: write to both simultaneously
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition, TourCompletionRecord } from './types';
import { dashboardFiltersTour } from './dashboardFiltersTour';
import { projectsFeatureTour } from './projectsFeatureTour';
import { variationsPageTour } from './variationsPageTour';
import { claimsReportPaymentsTour } from './claimsReportPaymentsTour';
import { progressClaimsTour } from './progressClaimsTour';
import { progressPaymentPanelTour } from './progressPaymentPanelTour';
import { projectImagesTour } from './projectImagesTour';
import { safeModeTour } from './safeModeTour';
import { paymentEnhancementsTour } from './paymentEnhancementsTour';
import { variationCommentsTour } from './variationCommentsTour';

// ─── Registry ────────────────────────────────────────────────────────────────

/**
 * All registered tours, sorted by priority.
 * Add new tour imports here as they are created.
 */
export const TOUR_REGISTRY: TourDefinition[] = [
  dashboardFiltersTour,
  projectsFeatureTour,
  variationsPageTour,
  claimsReportPaymentsTour,
  progressClaimsTour,
  progressPaymentPanelTour,
  // v2.5.0 tours
  projectImagesTour,
  safeModeTour,
  paymentEnhancementsTour,
  variationCommentsTour,
].sort((a, b) => a.priority - b.priority);

// ─── localStorage Helpers ────────────────────────────────────────────────────

const TOUR_STORAGE_KEY = 'seq_claims_tours_completed';

/** Get all completed tours from localStorage */
export function getCompletedTours(): TourCompletionRecord[] {
  try {
    const raw = localStorage.getItem(TOUR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Mark a tour as completed in localStorage */
export function markTourCompleted(tourId: string, skipped: boolean): void {
  const records = getCompletedTours();
  const existing = records.findIndex(r => r.tourId === tourId);
  const record: TourCompletionRecord = {
    tourId,
    completedAt: new Date().toISOString(),
    skipped,
  };
  if (existing >= 0) {
    records[existing] = record;
  } else {
    records.push(record);
  }
  localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(records));
}

/** Check if a specific tour has been completed (localStorage only) */
export function isTourCompleted(tourId: string): boolean {
  return getCompletedTours().some(r => r.tourId === tourId);
}

// ─── Server Persistence (Supabase JSONB) ─────────────────────────────────────

/**
 * Parse the server-stored tour completion records from the profile.
 * The `claims_completed_tours` column is JSONB, stored as an array.
 */
export function getServerCompletedTours(
  claimsCompletedTours: unknown,
): TourCompletionRecord[] {
  if (!claimsCompletedTours) return [];
  if (!Array.isArray(claimsCompletedTours)) return [];
  // Validate each record has the required shape
  return (claimsCompletedTours as Record<string, unknown>[]).filter(
    r => typeof r.tourId === 'string' && typeof r.completedAt === 'string',
  ) as unknown as TourCompletionRecord[];
}

/**
 * Merge localStorage and server completion records.
 * Server records take priority for conflicts (same tourId).
 * Returns the unified set.
 */
export function mergeCompletionRecords(
  localRecords: TourCompletionRecord[],
  serverRecords: TourCompletionRecord[],
): TourCompletionRecord[] {
  const merged = new Map<string, TourCompletionRecord>();

  // Local first (lower priority)
  for (const rec of localRecords) {
    merged.set(rec.tourId, rec);
  }
  // Server overwrites (higher priority, survives cache clears)
  for (const rec of serverRecords) {
    merged.set(rec.tourId, rec);
  }

  return Array.from(merged.values());
}

/**
 * Hydrate localStorage from server records.
 * Called on app load to restore completions after cache clears.
 */
export function hydrateLocalFromServer(
  claimsCompletedTours: unknown,
): void {
  const serverRecords = getServerCompletedTours(claimsCompletedTours);
  if (serverRecords.length === 0) return;

  const localRecords = getCompletedTours();
  const merged = mergeCompletionRecords(localRecords, serverRecords);

  // Only write if there are new records from server
  if (merged.length > localRecords.length) {
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(merged));
  }
}

/**
 * Check if a tour is completed in EITHER localStorage or server records.
 */
export function isTourCompletedAnywhere(
  tourId: string,
  serverRecords: TourCompletionRecord[],
): boolean {
  return (
    isTourCompleted(tourId) ||
    serverRecords.some(r => r.tourId === tourId)
  );
}

// ─── User Classification ────────────────────────────────────────────────────

/**
 * Determine if the user is "new" based on their profile creation date.
 * New = created within the last 7 days.
 */
export function isNewUser(profileCreatedAt: string | null): boolean {
  if (!profileCreatedAt) return true;
  const created = new Date(profileCreatedAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays <= 7;
}

// ─── Pending Tour Resolution ─────────────────────────────────────────────────

/**
 * Get the pending tours for the current page and user context.
 *
 * Logic:
 * 1. Filter by page
 * 2. Filter by audience (new vs returning user)
 * 3. Filter by version (only show if version > last seen)
 * 4. Filter out already-completed tours (localStorage + server)
 * 5. Sort by priority
 */
export function getPendingTours(
  page: string,
  lastSeenVersion: string | null,
  profileCreatedAt: string | null,
  serverCompletedTours?: TourCompletionRecord[],
): TourDefinition[] {
  const isNew = isNewUser(profileCreatedAt);
  const serverRecords = serverCompletedTours ?? [];

  return TOUR_REGISTRY.filter(tour => {
    // Must match the current page
    if (tour.page !== page) return false;

    // Audience check
    if (tour.audience === 'new_user' && !isNew) return false;
    if (tour.audience === 'returning_user' && isNew) return false;

    // Check BOTH localStorage and server for per-tour completion
    if (isTourCompletedAnywhere(tour.id, serverRecords)) return false;

    // Version gate: block tours from versions the user has already seen.
    // Once Supabase stores a version, ALL tours from that version or earlier
    // are permanently blocked, even if localStorage is cleared.
    // This guarantees production users see each tour exactly once.
    if (lastSeenVersion && compareVersions(tour.version, lastSeenVersion) <= 0) {
      return false;
    }

    return true;
  });
}

// ─── Utilities ───────────────────────────────────────────────────────────────

/**
 * Simple semver comparison.
 * Returns: -1 if a < b, 0 if equal, 1 if a > b
 */
export function compareVersions(a: string, b: string): number {
  const partsA = a.split('.').map(Number);
  const partsB = b.split('.').map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const numA = partsA[i] || 0;
    const numB = partsB[i] || 0;
    if (numA < numB) return -1;
    if (numA > numB) return 1;
  }
  return 0;
}
